'use client';

import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { useSession } from 'next-auth/react';

import type { ShippingEligibilityState, ProcessedShippingEligibility } from '@/lib/types/shippingEligibilityState';
import { ShippingEligibilityReducer } from '@/lib/context/shippingEligibilityReducer';
import {
  ShippingEligibilityActions,
  setEligibilitySuccess,
  setEligibilityError,
  startFetchEligibility,
} from '@/lib/context/shippingEligibilityActions';
import { defaultShippingEligibilityState } from '@/lib/context/shippingEligibilityDefaults';
import {
  checkProductShippability,
  type ProductShippabilityResult,
} from '@/lib/functions/checkProductShippability';
import { normalizeStateCode } from '@/lib/functions/normalizeStateCode';
import { normalizeZipCode } from '@/lib/functions/normalizeZipCode';
import { fetchShippingRulesForLocation } from '@/lib/functions/fetchShippingRulesForLocation';
import { setCookie } from '@/lib/functions/setCookie';
import { getCookie } from '@/lib/functions/getCookie';
import type { CheckProductShippabilityInput } from '@/lib/types/shipping';
import { DEFAULT_COUNTRY_CODE } from '@/lib/constants/countries';

export const ShippingEligibilityContext = createContext<{
  state: ShippingEligibilityState;
  dispatch: React.Dispatch<ShippingEligibilityActions>;
} | null>(null);

export const useShippingEligibility = () => {
  const context = React.useContext(ShippingEligibilityContext);
  if (!context) {
    throw new Error('useShippingEligibility must be used within a ShippingEligibilityProvider.');
  }
  return context;
};

export const useProductShippability = (input: CheckProductShippabilityInput): ProductShippabilityResult => {
  const { state } = useShippingEligibility();
  return checkProductShippability(input, state.eligibility, state.isLoading);
};

export { fetchShippingRulesForLocation } from '@/lib/functions/fetchShippingRulesForLocation';

export const useSaveShippingRulesToContext = () => {
  const { dispatch } = useShippingEligibility();

  return (rules: ProcessedShippingEligibility) => {
    setEligibilitySuccess(dispatch, rules);
  };
};

const COOKIE_KEY = `${process.env.NEXT_PUBLIC_SYLIUS_CHANNEL_CODE ?? 'PARTYSUPPLIES'}_ShippingEligibilityState`;
const CACHE_DURATION_MS = 30 * 60 * 1000;
const CACHE_VERSION = '2.0';
const COOKIE_LIFETIME_DAYS = 1;

interface CachedEligibilityData {
  data: ProcessedShippingEligibility;
  timestamp: number;
  version: string;
}

const getCachedEligibilityData = (): ProcessedShippingEligibility | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const cached = getCookie(COOKIE_KEY);
    if (!cached) {
      return null;
    }

    const parsedCache: CachedEligibilityData = JSON.parse(cached);
    if (Date.now() - parsedCache.timestamp > CACHE_DURATION_MS) {
      return null;
    }

    return parsedCache.data;
  } catch {
    return null;
  }
};

const setCachedEligibilityData = (data: ProcessedShippingEligibility): void => {
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const cacheData: CachedEligibilityData = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    setCookie(COOKIE_KEY, JSON.stringify(cacheData), COOKIE_LIFETIME_DAYS);
  } catch {
    // no-op
  }
};

export const useEnsureShippingRulesLoaded = () => {
  const { data: session } = useSession();
  const { state: shippingState, dispatch } = useShippingEligibility();

  const loadRulesForLocation = useCallback(async (
    provinceCode: string,
    postalCode: string,
    countryCode: string = DEFAULT_COUNTRY_CODE,
  ) => {
    const state = normalizeStateCode(provinceCode);
    const zipCode = normalizeZipCode(postalCode);
    const currentLocation = `${state}-${zipCode}-${countryCode.toUpperCase()}`;
    const contextLocation = shippingState.eligibility
      ? `${shippingState.eligibility.state}-${shippingState.eligibility.zipCode}-${countryCode.toUpperCase()}`
      : null;

    if (currentLocation === contextLocation || shippingState.isLoading) {
      return;
    }

    startFetchEligibility(dispatch);

    try {
      const rules = await fetchShippingRulesForLocation(provinceCode, postalCode, countryCode);
      setEligibilitySuccess(dispatch, rules);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch shipping eligibility';
      setEligibilityError(dispatch, errorMessage);
    }
  }, [dispatch, shippingState.eligibility, shippingState.isLoading]);

  const ensureRulesLoaded = useCallback(() => {
    const address = session?.user?.shippingAddress;
    if (!address?.provinceCode || !address?.postcode) {
      return;
    }

    loadRulesForLocation(
      address.provinceCode,
      address.postcode,
      address.countryCode ?? DEFAULT_COUNTRY_CODE,
    );
  }, [loadRulesForLocation, session?.user?.shippingAddress]);

  return {
    ensureRulesLoaded,
    loadRulesForLocation,
    isLoading: shippingState.isLoading,
    hasRules: !!shippingState.eligibility,
  };
};

export const usePageShippingRules = () => {
  const { ensureRulesLoaded, ...rest } = useEnsureShippingRulesLoaded();

  useEffect(() => {
    ensureRulesLoaded();
  }, [ensureRulesLoaded]);

  return {
    ensureRulesLoaded,
    ...rest,
  };
};

const ShippingEligibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(ShippingEligibilityReducer, defaultShippingEligibilityState, (initial) => {
    if (typeof document !== 'undefined') {
      const cached = getCachedEligibilityData();
      if (cached) {
        return {
          ...initial,
          eligibility: cached,
          lastFetch: Date.now(),
        };
      }
    }

    return initial;
  });

  useEffect(() => {
    if (state.eligibility) {
      setCachedEligibilityData(state.eligibility);
    }
  }, [state.eligibility]);

  return (
    <ShippingEligibilityContext.Provider value={{ state, dispatch }}>
      {children}
    </ShippingEligibilityContext.Provider>
  );
};

export default ShippingEligibilityProvider;
